import { EventEmitter } from 'events';

export enum WebSocketReadyState {
    CONNECTING = 0,
    OPEN = 1,
    CLOSING = 2,
    CLOSED = 3
}

export interface WebSocketMessage {
    type: string;
    [key: string]: any;
}

export interface LocationUpdate {
    driver_id: number;
    latitude: number;
    longitude: number;
    heading: number;
    speed: number;
    accuracy: number;
    battery_level: number;
    is_available: boolean;
    is_online: boolean;
    timestamp: string;
}

export interface TripStatusUpdate {
    trip_id: number;
    status: string;
    driver_id?: number;
    timestamp: string;
}

export interface ConnectionStatusUpdate {
    trip_id: number;
    is_connected: boolean;
    timestamp: string;
}

export interface OfflineSyncResult {
    success: boolean;
    processed_count: number;
    total_count: number;
    errors: string[];
    timestamp: string;
}

class WebSocketService extends EventEmitter {
    private socket: WebSocket | null = null;
    private url: string;
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 5;
    private reconnectInterval: number = 3000;
    private reconnectTimeoutId: number | null = null;
    private heartbeatInterval: number | null = null;
    private isReconnecting: boolean = false;

    constructor(url: string) {
        super();
        this.url = url;
    }

    public connect(token?: string): void {
        if (!token && !localStorage.getItem('token')) {
            console.warn('WebSocket: No hay token de autenticación, conexión cancelada');
            return;
        }

        if (this.socket && this.socket.readyState === WebSocketReadyState.OPEN) {
            console.log('WebSocket ya está conectado');
            return;
        }

        if (this.socket) {
            this.socket.close();
        }

        try {
            const wsUrl = this.getAuthenticatedUrl(token);
            console.log(`Conectando WebSocket a: ${wsUrl}`);

            this.socket = new WebSocket(wsUrl);
            this.socket.onopen = this.handleOpen.bind(this);
            this.socket.onmessage = this.handleMessage.bind(this);
            this.socket.onclose = this.handleClose.bind(this);
            this.socket.onerror = this.handleError.bind(this);
        } catch (error) {
            console.error('Error al crear conexión WebSocket:', error);
            if (token || localStorage.getItem('token')) {
                this.scheduleReconnect(token);
            }
        }
    }

    private getAuthenticatedUrl(token?: string): string {
        const authToken = token || localStorage.getItem('token');
        return `${this.url}?token=${authToken || ''}`;
    }

    private handleOpen(event: Event): void {
        console.log('WebSocket conectado');
        this.reconnectAttempts = 0;
        this.emit('connected');
        this.startHeartbeat();
    }

    private startHeartbeat(): void {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        this.heartbeatInterval = window.setInterval(() => {
            this.sendMessage({ type: 'heartbeat' });
        }, 30000);
    }

    private handleMessage(event: MessageEvent): void {
        try {
            const data = JSON.parse(event.data) as WebSocketMessage;
            console.log('WebSocket mensaje recibido:', data);
            this.emit('message', data);

            if (data.type) {
                this.emit(data.type, data);
                this.handleSpecificMessageTypes(data);
            }
        } catch (error) {
            console.error('Error al procesar mensaje WebSocket:', error);
        }
    }

    private handleSpecificMessageTypes(data: WebSocketMessage): void {
        switch (data.type) {
            case 'location_update':
                this.emit('driverLocationUpdate', data);
                break;

            case 'trip_status_update':
                this.emit('tripStatusUpdate', data);
                break;

            case 'connection_status_changed':
                this.emit('connectionStatusUpdate', data);
                break;

            case 'offline_sync_result':
                this.emit('offlineSyncResult', data);
                break;

            case 'notification':
                this.emit('notification', {
                    message: data.message,
                    type: data.notification_type,
                    entity_id: data.entity_id
                });
                break;

            case 'error':
                this.emit('error', new Error(data.message || 'Error en WebSocket'));
                break;
        }
    }

    private handleClose(event: CloseEvent): void {
        console.log(`WebSocket desconectado: ${event.code} ${event.reason}`);

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        this.emit('disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
        });

        if (event.code !== 1000 && !this.isReconnecting) {
            this.scheduleReconnect();
        }
    }

    private handleError(event: Event): void {
        console.error('Error en la conexión WebSocket:', event);
        this.emit('error', event);
    }

    private scheduleReconnect(token?: string): void {
        if (this.isReconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                console.error(`Máximo número de intentos de reconexión (${this.maxReconnectAttempts}) alcanzado`);
                this.emit('reconnectFailed');
            }
            return;
        }

        if (!token && !localStorage.getItem('token')) {
            console.warn('WebSocket: No hay token para reconexión');
            return;
        }

        this.isReconnecting = true;

        const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts);
        console.log(`Programando reconexión WebSocket en ${delay}ms (intento ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }

        this.reconnectTimeoutId = window.setTimeout(() => {
            this.reconnectAttempts++;
            this.isReconnecting = false;
            this.emit('reconnecting', this.reconnectAttempts);
            this.connect(token);
        }, delay);
    }

    public sendMessage(message: WebSocketMessage): boolean {
        if (!this.socket || this.socket.readyState !== WebSocketReadyState.OPEN) {
            console.error('No se puede enviar mensaje: WebSocket no está conectado');
            return false;
        }

        try {
            this.socket.send(JSON.stringify(message));
            return true;
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            return false;
        }
    }

    public sendLocationUpdate(data: {
        latitude: number;
        longitude: number;
        heading: number;
        speed: number;
        accuracy: number;
        battery_level?: number;
        is_available?: boolean;
    }): boolean {
        return this.sendMessage({
            type: 'location_update',
            ...data,
            timestamp: new Date().toISOString()
        });
    }

    public sendTripStatusUpdate(tripId: number, status: string): boolean {
        return this.sendMessage({
            type: 'trip_status_update',
            trip_id: tripId,
            status,
            timestamp: new Date().toISOString()
        });
    }

    public sendOfflineLocations(locations: Array<{
        latitude: number;
        longitude: number;
        speed: number;
        heading: number;
        accuracy: number;
        is_connected: boolean;
        recorded_at: string;
    }>): boolean {
        return this.sendMessage({
            type: 'offline_locations_sync',
            locations
        });
    }

    public sendConnectionStatusUpdate(isConnected: boolean, createStatusRecord: boolean = true): boolean {
        return this.sendMessage({
            type: 'connection_status_update',
            is_connected: isConnected,
            create_status_record: createStatusRecord
        });
    }

    public subscribeToTrip(tripId: number): boolean {
        return this.sendMessage({
            type: 'subscribe_trip',
            trip_id: tripId
        });
    }

    public subscribeToDriver(driverId: number): boolean {
        return this.sendMessage({
            type: 'subscribe_driver',
            driver_id: driverId
        });
    }

    public subscribeToArea(bounds: {
        south: number;
        west: number;
        north: number;
        east: number;
    }): boolean {
        return this.sendMessage({
            type: 'subscribe_area',
            bounds
        });
    }

    public requestTripStatus(tripId: number): boolean {
        return this.sendMessage({
            type: 'request_trip_status',
            trip_id: tripId
        });
    }

    public disconnect(): void {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        if (this.socket) {
            this.socket.close(1000, 'Cierre solicitado por cliente');
            this.socket = null;
        }

        this.isReconnecting = false;
        this.reconnectAttempts = 0;
    }

    public getState(): WebSocketReadyState {
        return this.socket ? this.socket.readyState : WebSocketReadyState.CLOSED;
    }

    public isConnected(): boolean {
        return this.socket?.readyState === WebSocketReadyState.OPEN;
    }
}

const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
const WS_PATH = process.env.REACT_APP_WS_PATH || '/ws/drivers/locations/';

export const webSocketService = new WebSocketService(`${WS_BASE_URL}${WS_PATH}`);


export default webSocketService;